import { PromptType } from '@communities/prompt-management/domain';
import { usePromptDetails, useUpdateProdPromptVersion } from '@communities/prompt-management/hooks';
import { parsePromptVersionDate } from '@communities/prompt-management/utils';
import { ErrorBanner, TimestampDisplay } from '@core/components';
import type { DomainError } from '@core/domain';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Badge, BadgeVariant, Dialog, Heading, LoadingSpinner, Spinner } from '@soteria/design-system-ve';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { PromptTypeTabs } from './PromptTypeTabs';
import * as styles from './ViewPromptDialog.css';

interface PromptContentInput {
  loading: boolean;
  errors: DomainError[] | null;
  details: { prompt: string } | null | undefined;
  errorTitle: string;
  emptyMessage: string;
}

/** Render prompt content with loading/error/empty handling */
function renderPromptContent({ loading, errors, details, errorTitle, emptyMessage }: PromptContentInput): ReactNode {
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="medium" />
      </div>
    );
  }

  if (errors && errors.length > 0) {
    return (
      <div className={styles.errorContainer}>
        <ErrorBanner errors={errors} title={errorTitle} />
      </div>
    );
  }

  return (
    <div className={styles.promptContent}>
      <pre className={styles.promptCodeBlock}>{details?.prompt || emptyMessage}</pre>
    </div>
  );
}

interface ViewPromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  promptVersion?: string;
  isProductionPrompt: boolean;
  communityId?: string;
  isCommunityAdmin?: boolean;
  promptType?: PromptType;
}

/**
 * View Prompt Dialog
 *
 * Displays prompt details for a given version. When promptType is Username,
 * shows a single prompt view. Otherwise shows classification/moderation tabs.
 */
// eslint-disable-next-line complexity
function ViewPromptDialogComponent({ isOpen, onClose, promptVersion, isProductionPrompt, communityId, isCommunityAdmin, promptType }: ViewPromptDialogProps) {
  const isUsernameMode = promptType === PromptType.Username;
  const [activeTab, setActiveTab] = useState<PromptType>(PromptType.Classification);

  const classificationInput = !isUsernameMode && promptVersion ? { promptVersion, type: PromptType.Classification } : undefined;
  const {
    promptDetails: classificationDetails,
    loading: classificationLoading,
    errors: classificationErrors,
  } = usePromptDetails(communityId, classificationInput);

  const moderationInput = !isUsernameMode && activeTab === PromptType.Moderation && promptVersion ? { promptVersion, type: PromptType.Moderation } : undefined;
  const { promptDetails: moderationDetails, loading: moderationLoading, errors: moderationErrors } = usePromptDetails(communityId, moderationInput);

  const usernameInput = isUsernameMode && promptVersion ? { promptVersion, type: PromptType.Username } : undefined;
  const { promptDetails: usernameDetails, loading: usernameLoading, errors: usernameErrors } = usePromptDetails(communityId, usernameInput);

  const { updatePromptProdVersion, loading: isUpdating, errors: updateErrors, resetErrors } = useUpdateProdPromptVersion();

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      resetErrors();
      setActiveTab(PromptType.Classification);
    }
  }, [isOpen, setActiveTab, resetErrors]);

  // Handle tab change
  const handleTabChange = useCallback((tab: PromptType) => {
    setActiveTab(tab);
  }, []);

  const handleMakeProduction = useCallback(async () => {
    resetErrors();

    if (communityId && promptVersion) {
      const input = isUsernameMode ? { version: promptVersion, type: PromptType.Username } : { version: promptVersion };

      await updatePromptProdVersion(communityId, input);
    }
  }, [resetErrors, communityId, promptVersion, isUsernameMode, updatePromptProdVersion]);

  if (!promptVersion) {
    return null;
  }

  const promptDate = parsePromptVersionDate(promptVersion);

  const customHeader = (
    <div>
      {/* Title with production badge */}
      <div className={styles.headerTitleContainer}>
        <Heading level="h2">
          {isUsernameMode ? 'Username Prompt' : 'Version'} {promptVersion}
        </Heading>
        {isProductionPrompt && <Badge variant={BadgeVariant.Approved}>PRODUCTION</Badge>}
      </div>
      {/* Created info */}
      <div className={styles.createdInfo}>Created {promptDate ? <TimestampDisplay timestamp={promptDate} /> : promptVersion}</div>
    </div>
  );

  return (
    <Dialog
      cancelAction={{
        label: 'Close',
        disabled: false,
      }}
      customHeader={customHeader}
      isOpen={isOpen}
      primaryAction={
        !isProductionPrompt && isCommunityAdmin
          ? {
              label: isUpdating ? <LoadingSpinner /> : 'Make Production',
              onClick: handleMakeProduction,
              disabled: isUpdating,
              variant: 'accent',
            }
          : undefined
      }
      size="xl"
      onClose={onClose}
    >
      <div className={styles.promptDetails}>
        {/* Error Banner */}
        {updateErrors && updateErrors.length > 0 && (
          <div className={styles.errorBannerWrapper}>
            <ErrorBanner errors={updateErrors} title="Failed to update prompt:" />
          </div>
        )}

        {isUsernameMode ? (
          /* Username prompt - single view, no tabs */
          <div className={styles.promptSection}>
            {renderPromptContent({
              loading: usernameLoading,
              errors: usernameErrors,
              details: usernameDetails,
              errorTitle: 'Failed to load username prompt',
              emptyMessage: 'No username prompt available',
            })}
          </div>
        ) : (
          /* Classification/Moderation tabs */
          <>
            <PromptTypeTabs activeTab={activeTab} onTabChange={handleTabChange} />

            <div className={styles.promptSection}>
              {activeTab === PromptType.Classification
                ? renderPromptContent({
                    loading: classificationLoading,
                    errors: classificationErrors,
                    details: classificationDetails,
                    errorTitle: 'Failed to load classification prompt',
                    emptyMessage: '',
                  })
                : renderPromptContent({
                    loading: moderationLoading,
                    errors: moderationErrors,
                    details: moderationDetails,
                    errorTitle: 'Failed to load moderation prompt',
                    emptyMessage: 'No moderation prompt available',
                  })}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

export const ViewPromptDialog = withAppInsightsTracking(ViewPromptDialogComponent, 'ViewPromptDialog');
